import type { QueryReturnValue } from '@reduxjs/toolkit/query'
import { baseApi } from '@/lib/store/api/baseQuery'
import { apiEndpoints } from '@/lib/api/endpoints'
import { sortedPendingQuestionnaireCriteria, soonestPendingQuestionnaireDeadline } from '@/lib/careJourney'
import { getCareJourneyChecklist } from '@/lib/careJourney/checklist'
import type { PrescriptionCriteriaItem } from '@/types/interfaces/prescriptionDetail'
import type { QuestionnaireResponse } from '@/types/interfaces/sku'

function careJourneyNextStepsHttpStatus(error: unknown): number | undefined {
    if (!error || typeof error !== 'object') return undefined
    const record = error as { status?: unknown; originalStatus?: unknown }
    if (typeof record.status === 'number') return record.status
    if (typeof record.originalStatus === 'number') return record.originalStatus
    return undefined
}

function isCareJourneyFeatureDisabledError(error: unknown): boolean {
    return careJourneyNextStepsHttpStatus(error) === 404
}

// RTK `queryFn` supplies a single-arg baseQuery wrapper (not the full 3-arg BaseQueryFn).
type CareJourneyQueryBaseQuery = (args: string | { url: string; method?: string }) => unknown

async function fetchCareJourneyNextStepsData(
    baseQuery: CareJourneyQueryBaseQuery,
): Promise<{ data: CareJourneyNextStepsResponse } | { error: unknown }> {
    const nextStepsResult = (await baseQuery(
        apiEndpoints.careJourney.nextSteps,
    )) as QueryReturnValue<unknown, unknown>
    if (!nextStepsResult.error) {
        return { data: nextStepsResult.data as CareJourneyNextStepsResponse }
    }
    if (isCareJourneyFeatureDisabledError(nextStepsResult.error)) {
        return { data: EMPTY_CARE_JOURNEY_NEXT_STEPS }
    }
    return { error: nextStepsResult.error }
}

export interface CareJourneySummary {
    actionable_count: number
    estimated_minutes?: number | null
    soonest_deadline?: string | null
    subtitle?: string | null
}

export interface CareJourneyChecklistItem extends PrescriptionCriteriaItem {
    type?: 'Lab' | 'Questionnaire' | 'Visit'
    estimated_minutes?: number | null
    medication_names: string[]
    medication_display_names?: string[]
    medication_ids: string[]
    lab_bundle?: boolean
    lab_panel_names?: string[]
    lab_sku_count?: number
    lab_sku_ids?: string[]
}

export interface CareJourneyGroup {
    type: 'Lab' | 'Questionnaire' | 'Visit'
    title: string
    count: number
    locked: boolean
    available_at: string | null
    soonest_deadline: string | null
    estimated_minutes: number | null
    medication_names: string[]
    medication_display_names?: string[]
    completed_count: number
    total_count: number
    criteria: CareJourneyChecklistItem[]
}

export interface CareJourneyNextStepsResponse {
    summary: CareJourneySummary
    checklist: CareJourneyChecklistItem[]
    groups: CareJourneyGroup[]
}

export const EMPTY_CARE_JOURNEY_NEXT_STEPS: CareJourneyNextStepsResponse = {
    summary: { actionable_count: 0 },
    checklist: [],
    groups: [],
}

export interface MedicationCheckInLabBundleResponse {
    lab_order_id: string
    skus: Array<{ id: number; name: string }>
}

export interface MedicationCheckInLabUploadCompletionResponse {
    submitted_at: string
}

export interface CareJourneyQuestionnaireCardMeta {
    questionCount: number
    soonestDeadline: string | null
}

export const careJourneyApi = baseApi.injectEndpoints({
    endpoints: (builder) => ({
        getCareJourneyNextSteps: builder.query<CareJourneyNextStepsResponse, void>({
            async queryFn(_arg, _api, _extraOptions, baseQuery) {
                return fetchCareJourneyNextStepsData(baseQuery)
            },
            providesTags: ['CareJourney'],
        }),

        // Live questionnaire card fields for the dashboard. The dashboard payload
        // can go stale after partial progress through the combined Care Journey
        // flow; refetch this on home and next-steps mount for an accurate count and
        // soonest pending deadline.
        getCareJourneyQuestionnaireCardMeta: builder.query<CareJourneyQuestionnaireCardMeta, void>({
            async queryFn(_arg, _api, _extraOptions, baseQuery) {
                const nextStepsResult = await fetchCareJourneyNextStepsData(baseQuery)
                if ('error' in nextStepsResult) {
                    return { error: nextStepsResult.error }
                }
                const data = nextStepsResult.data
                // Pending questionnaire work can surface on the top-level `checklist`
                // while `group.criteria` is empty, so source the count and deadline from
                // the unified checklist to avoid zeroing out the card after the live refetch.
                const pendingQuestionnaires = sortedPendingQuestionnaireCriteria(getCareJourneyChecklist(data))
                if (pendingQuestionnaires.length === 0) {
                    return { data: { questionCount: 0, soonestDeadline: null } }
                }
                const questionnaireGroup = (data?.groups ?? []).find(
                    (candidate) => candidate.type === 'Questionnaire' && !candidate.locked,
                )

                const lengths = await Promise.all(
                    pendingQuestionnaires.map(async (criterion) => {
                        const result = (await baseQuery(
                            apiEndpoints.questionnaires.getQuestion(criterion.required_id, '1'),
                        )) as QueryReturnValue<unknown, unknown>
                        if (result.error) return 0
                        return (result.data as QuestionnaireResponse)?.questionnaire_length ?? 0
                    }),
                )
                return {
                    data: {
                        questionCount: lengths.reduce((sum, length) => sum + length, 0),
                        soonestDeadline:
                            soonestPendingQuestionnaireDeadline(pendingQuestionnaires) ??
                            questionnaireGroup?.soonest_deadline ??
                            null,
                    },
                }
            },
            providesTags: ['CareJourney'],
        }),
        // Account-wide bundle spans every renewal medication, so refresh the
        // prescription detail/check-in pages too (general Prescription tag
        // invalidates all cached prescription queries).
        createMedicationCheckInLabBundle: builder.mutation<MedicationCheckInLabBundleResponse, void>({
            query: () => ({
                url: apiEndpoints.careJourney.labBundle,
                method: 'POST',
            }),
            invalidatesTags: ['CareJourney', 'Dashboard', 'Prescription'],
        }),
        completeMedicationCheckInLabUpload: builder.mutation<MedicationCheckInLabUploadCompletionResponse, void>({
            query: () => ({
                url: apiEndpoints.careJourney.completeLabUpload,
                method: 'POST',
            }),
            invalidatesTags: ['CareJourney', 'Dashboard', 'Prescription'],
        }),
        shareMedicationCheckInLabUploadForBooking: builder.mutation<
            MedicationCheckInLabUploadCompletionResponse,
            void
        >({
            query: () => ({
                url: apiEndpoints.careJourney.shareLabUploadForBooking,
                method: 'POST',
            }),
            invalidatesTags: ['CareJourney', 'Dashboard', 'MedicationCheckInLabUpload', 'Prescription'],
        }),
    }),
    overrideExisting: false,
})

export const {
    useGetCareJourneyNextStepsQuery,
    useGetCareJourneyQuestionnaireCardMetaQuery,
    useCreateMedicationCheckInLabBundleMutation,
    useCompleteMedicationCheckInLabUploadMutation,
    useShareMedicationCheckInLabUploadForBookingMutation,
} = careJourneyApi
